// 開発環境のみで開発者コントロールパネルをインポート
import generateRequestParams from '@src/api/core/get_request_params';
import { FeatureFlag, useFeatureFlag } from '@src/config/featureFlags';
import { usePdfToastNotifications } from '@src/hooks/usePdfToastNotifications';
import { usePreviewVisibility } from '@src/hooks/usePreviewVisibility';
import { usePdfGeneration } from '@src/modules/react_hooks/usePdfGeneration';
import { useRirekishoPdfGeneration } from '@src/modules/react_hooks/useRirekishoPdfGeneration';
import { useTranslation } from '@src/modules/react_hooks/useTranslation';
import { selectRirekishoData } from '@src/selectors/rirekishoDataSelector';
import store from '@src/store/index';
import { selectFocusMode } from '@src/store/slices/focus_mode';
import { selectRequesting } from '@src/store/slices/requesting';
import { addToast } from '@src/store/slices/toastSlice';
import { selectTypoCheckEnabled } from '@src/store/slices/typo_check';
import { selectRefreshPdf, updateRefreshPdf } from '@src/store/slices/ui';
// Types
import type { GeneratePdfApiParams } from '@src/types/shared/api/pdf_api_params';
import { blurActiveInputLikeElement } from '@src/utils/dom/inputElement';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

export const useAppPdfGeneration = (isDesktopView: boolean) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  const path = location.pathname;

  // Feature Flags
  const isRirekishoEnabled = useFeatureFlag(FeatureFlag.USE_RIREKISHO);
  const isResumeEnabled = useFeatureFlag(FeatureFlag.USE_RESUME);
  const shouldShowPreview = usePreviewVisibility(path);
  const isRirekishoMode = isRirekishoEnabled && path.startsWith('/rirekisho');
  const isFocusMode = useSelector(selectFocusMode) || false;

  // PDF States
  const [pdfPage, setPdfPage] = useState<number>(1);
  const [pdfPageTotal, setPdfPageTotal] = useState<number>(1);
  const [isPreviewDrawerOpen, setIsPreviewDrawerOpen] = useState<boolean>(false);
  const [isPdfScrolling, setIsPdfScrolling] = useState<boolean>(false);
  const [previewToken, setPreviewToken] = useState<string | null>(null);
  const [isPreviewGenerating, setIsPreviewGenerating] = useState<boolean>(false);

  // Redux Selectors
  const refreshPdf = useSelector(selectRefreshPdf);
  const requesting = useSelector(selectRequesting);
  const typoCheckEnabled = useSelector(selectTypoCheckEnabled);

  // PDF Generation Hooks
  const pdfGeneration = usePdfGeneration();
  const rirekishoPdfGeneration = useRirekishoPdfGeneration();
  const { generatePdfDebounced, generatePdfAsync, isPending: isResumeGenerating } = pdfGeneration;
  const {
    generateRirekishoPdfDebounced,
    generateRirekishoPdfAsync,
    isPending: isRirekishoGenerating,
  } = rirekishoPdfGeneration;

  // Toast Notifications
  const resumeToastMessages = useMemo(
    () => ({
      pending: t('messages.generatingResume'),
      success: t('messages.resumeGenerated'),
      error: t('messages.resumeGenerateError'),
    }),
    [t]
  );

  const rirekishoToastMessages = useMemo(
    () => ({
      pending: t('messages.generatingRirekisho'),
      success: t('messages.rirekishoGenerated'),
      error: t('messages.rirekishoGenerateError'),
    }),
    [t]
  );

  usePdfToastNotifications(
    isResumeEnabled
      ? {
          isPending: pdfGeneration.isPending,
          isSuccess: pdfGeneration.isSuccess,
          isError: pdfGeneration.isError,
        }
      : { isPending: false, isSuccess: false, isError: false },
    resumeToastMessages
  );

  usePdfToastNotifications(
    isRirekishoEnabled
      ? {
          isPending: rirekishoPdfGeneration.isPending,
          isSuccess: rirekishoPdfGeneration.isSuccess,
          isError: rirekishoPdfGeneration.isError,
        }
      : { isPending: false, isSuccess: false, isError: false },
    rirekishoToastMessages
  );

  // Helper Functions
  const waitForTypoChecks = useCallback(async (): Promise<boolean> => {
    if (!typoCheckEnabled) {
      return true;
    }

    const timeoutMs = 5000;
    const intervalMs = 100;
    const quietPeriodMs = 150;
    const timeoutAt = Date.now() + timeoutMs;

    let quietStart = store.getState().typoCheck.runningCount === 0 ? Date.now() : 0;

    while (Date.now() < timeoutAt) {
      const runningCount = store.getState().typoCheck.runningCount;

      if (runningCount === 0) {
        if (quietStart === 0) {
          quietStart = Date.now();
        }

        if (Date.now() - quietStart >= quietPeriodMs) {
          return true;
        }
      } else {
        quietStart = 0;
      }

      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }

    return false;
  }, [typoCheckEnabled]);

  const blurActiveInput = useCallback(blurActiveInputLikeElement, []);

  // Focus Mode Transition
  const prevFocusModeRef = useRef(isFocusMode);
  useEffect(() => {
    if (prevFocusModeRef.current && !isFocusMode) {
      setTimeout(() => {
        store.dispatch(updateRefreshPdf(true));
      }, 100);
    }
    prevFocusModeRef.current = isFocusMode;
  }, [isFocusMode]);

  // PDF Generation Handling
  const isPdfGenerationSchedulingRef = useRef(false);
  const lastRirekishoDataHashRef = useRef<string>('');
  const lastResumeDataHashRef = useRef<string>('');

  const handlePdfGeneration = useCallback(async (): Promise<void> => {
    if (!isDesktopView) {
      return;
    }
    if (!shouldShowPreview || !refreshPdf || requesting || isFocusMode) {
      return;
    }
    if (isPdfGenerationSchedulingRef.current) {
      return;
    }
    if (isResumeGenerating || isRirekishoGenerating) {
      return;
    }

    isPdfGenerationSchedulingRef.current = true;

    try {
      const didBlurActive = blurActiveInput();
      if (didBlurActive) {
        await new Promise((resolve) => setTimeout(resolve, 0));
      }

      const typoCompleted = await waitForTypoChecks();

      if (!typoCompleted) {
        dispatch(
          addToast({
            message: t('messages.typoWaitTimeout'),
            severity: 'warning',
            duration: 4000,
          })
        );
        store.dispatch(updateRefreshPdf(false));
        return;
      }

      if (isRirekishoMode && isRirekishoEnabled) {
        const latestRirekishoData = selectRirekishoData(store.getState());
        const dataHash = JSON.stringify(latestRirekishoData);

        if (dataHash === lastRirekishoDataHashRef.current) {
          store.dispatch(updateRefreshPdf(false));
          return;
        }

        lastRirekishoDataHashRef.current = dataHash;
        generateRirekishoPdfDebounced(latestRirekishoData);
      } else if (!isRirekishoMode && isResumeEnabled) {
        const params: GeneratePdfApiParams = generateRequestParams(store.getState());
        const dataHash = JSON.stringify(params);

        if (dataHash === lastResumeDataHashRef.current) {
          store.dispatch(updateRefreshPdf(false));
          return;
        }

        lastResumeDataHashRef.current = dataHash;
        generatePdfDebounced(params);
      }
    } finally {
      isPdfGenerationSchedulingRef.current = false;
    }
  }, [
    isDesktopView,
    shouldShowPreview,
    refreshPdf,
    requesting,
    isFocusMode,
    dispatch,
    isRirekishoMode,
    isRirekishoEnabled,
    isResumeEnabled,
    generatePdfDebounced,
    generateRirekishoPdfDebounced,
    isResumeGenerating,
    isRirekishoGenerating,
    waitForTypoChecks,
    blurActiveInput,
    t,
  ]);

  useEffect(() => {
    void handlePdfGeneration();
  }, [handlePdfGeneration]);

  // Handlers
  const handlePdfPageChange = useCallback((newPage: number): void => {
    setPdfPage(newPage);
  }, []);

  const handlePreviewClose = useCallback((): void => {
    setIsPreviewDrawerOpen(false);
    setPreviewToken(null);
    setIsPreviewGenerating(false);
  }, []);

  const handlePreviewToggle = useCallback(async (): Promise<void> => {
    if (isPreviewDrawerOpen) {
      handlePreviewClose();
      return;
    }

    if (isPreviewGenerating) {
      return;
    }

    setIsPreviewGenerating(true);

    try {
      const didBlurActive = blurActiveInput();
      if (didBlurActive) {
        await new Promise((resolve) => setTimeout(resolve, 0));
      }

      const typoCompleted = await waitForTypoChecks();
      if (!typoCompleted) {
        dispatch(
          addToast({
            message: t('messages.typoWaitTimeout'),
            severity: 'warning',
            duration: 4000,
          })
        );
        setIsPreviewGenerating(false);
        return;
      }

      let pdfResult: { token: string } | null = null;
      if (isRirekishoMode && isRirekishoEnabled) {
        const latestRirekishoData = selectRirekishoData(store.getState());
        pdfResult = await generateRirekishoPdfAsync(latestRirekishoData);
      } else if (!isRirekishoMode && isResumeEnabled) {
        const params: GeneratePdfApiParams = generateRequestParams(store.getState());
        pdfResult = await generatePdfAsync(params);
      }

      const resolvedToken = pdfResult?.token || null;

      if (!resolvedToken) {
        throw new Error('PDFトークンが取得できませんでした');
      }

      setPreviewToken(resolvedToken);
      setIsPreviewDrawerOpen(true);
    } catch (error) {
      console.error('PDF生成エラー:', error);
      dispatch(
        addToast({
          message: t('messages.resumeGenerateError'),
          severity: 'error',
          duration: 4000,
        })
      );
      setIsPreviewGenerating(false);
    }
  }, [
    isPreviewDrawerOpen,
    isPreviewGenerating,
    isRirekishoMode,
    isRirekishoEnabled,
    isResumeEnabled,
    blurActiveInput,
    waitForTypoChecks,
    generatePdfAsync,
    generateRirekishoPdfAsync,
    dispatch,
    t,
    handlePreviewClose,
  ]);

  return {
    // States
    pdfPage,
    setPdfPage,
    pdfPageTotal,
    setPdfPageTotal,
    isPreviewDrawerOpen,
    setIsPreviewDrawerOpen,
    isPdfScrolling,
    setIsPdfScrolling,
    previewToken,
    isPreviewGenerating,
    setIsPreviewGenerating,
    shouldShowPreview,
    waitForTypoChecks,

    // Handlers
    handlePdfPageChange,
    handlePreviewToggle,
    handlePreviewClose,
  };
};
